// Lightweight in-call signals: reactions (F-COM-3, lossy channel) and raise
// hand (F-COM-4, reliable). Same wire discipline as chat: JSON, versioned,
// validated on receive.

export const REACTION_TOPIC = 'bz.reaction';
export const HAND_TOPIC = 'bz.hand';

// Reactions are constrained to a fixed set — nothing free-form crosses the
// wire, which also makes rate-limit UX predictable.
export const REACTIONS = ['👍', '👏', '❤️', '😂', '🎉', '😮'] as const;
export type Reaction = (typeof REACTIONS)[number];

export interface ReactionWire {
  v: 1;
  emoji: Reaction;
}

export interface HandWire {
  v: 1;
  raised: boolean;
  ts: number;
  /** Set when a host lowers someone else's hand (F-COM-4). Cosmetic-level
   * control: the UI only offers it to hosts; it carries no media privilege. */
  target?: string;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function encodeReaction(emoji: Reaction): Uint8Array<ArrayBuffer> {
  return new Uint8Array(encoder.encode(JSON.stringify({ v: 1, emoji } satisfies ReactionWire)));
}

export function decodeReaction(payload: Uint8Array): Reaction | null {
  try {
    const parsed = JSON.parse(decoder.decode(payload)) as ReactionWire;
    return parsed.v === 1 && (REACTIONS as readonly string[]).includes(parsed.emoji)
      ? parsed.emoji
      : null;
  } catch {
    return null;
  }
}

export function encodeHand(raised: boolean, target?: string): Uint8Array<ArrayBuffer> {
  return new Uint8Array(
    encoder.encode(JSON.stringify({ v: 1, raised, ts: Date.now(), target } satisfies HandWire)),
  );
}

export function decodeHand(payload: Uint8Array): HandWire | null {
  try {
    const parsed = JSON.parse(decoder.decode(payload)) as HandWire;
    return parsed.v === 1 && typeof parsed.raised === 'boolean' ? parsed : null;
  } catch {
    return null;
  }
}

/** Client-side reaction rate limiter (F-COM-3): N per rolling window. */
export class RateGate {
  private stamps: number[] = [];
  constructor(
    private max: number,
    private windowMs: number,
  ) {}

  tryPass(now = Date.now()): boolean {
    this.stamps = this.stamps.filter((t) => now - t < this.windowMs);
    if (this.stamps.length >= this.max) return false;
    this.stamps.push(now);
    return true;
  }
}
